import { IAuditAppointment } from "audit/models";
import { isEmpty } from "lodash";
import { useState } from "react";

type Poi = {
  key: string;
  location: google.maps.LatLngLiteral;
  scheduled: boolean;
};

export const useGetGeocoder = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [data, setData] = useState<Poi[]>([]);

  const getPois = async (
    geocodingLib: google.maps.GeocodingLibrary,
    addresses: IAuditAppointment[]
  ) => {
    try {
      setLoading(true);
      setSuccess(false);
      setError(undefined);
      setData([]);

      const geocoder = new geocodingLib.Geocoder();
      const pois = await convertAddressesToGeocodes(geocoder, addresses);

      setData(pois);
      setSuccess(true);
    } catch (error) {
      setSuccess(false);
      setError(error as Error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const addressToGeocode = (
    geocoder: google.maps.Geocoder,
    address: string
  ): Promise<google.maps.LatLngLiteral> =>
    new Promise((resolve, reject) => {
      geocoder.geocode(
        { address: address },
        (results: any, status: google.maps.GeocoderStatus) => {
          if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
            return reject(new Error(`Geocoding failed: ${status}`));
          }

          const latitude = results[0].geometry.location.lat();
          const longitude = results[0].geometry.location.lng();
          resolve({ lat: latitude, lng: longitude });
        }
      );
    });

  const convertAddressesToGeocodes = async (
    geocoder: google.maps.Geocoder,
    locations: IAuditAppointment[]
  ): Promise<Poi[]> => {
    const results = await Promise.allSettled(
      locations.map(async (l) => ({
        key: l.projectName,
        location: await addressToGeocode(geocoder, l.address),
        scheduled: !isEmpty(l.auditScheduleDate),
      }))
    );

    return results
      .filter((r): r is PromiseFulfilledResult<Poi> => r.status === "fulfilled")
      .map((r) => r.value);
  };

  return {
    getPois,
    loading,
    success,
    error,
    data,
  };
};
